import { Checkbox, Flex, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import { useState } from "react";
import { useEffect } from "react";
import FooterModal from "ui/components/Button/FooterModal";
import FormAssinaturas, {
  AssinaturasItem,
} from "components/Relatorio/FormAssinaturas";
import { useForm } from "react-hook-form";

interface ModalSelectFieldsRelatorioProps<T> {
  fields: {
    value: T;
    label: string;
    defaultChecked?: boolean;
  }[];
  groupByFields?: {
    value: T;
    label: string;
  }[];
  extraFields?: {
    value: string;
    label: string;
    defaultChecked?: boolean;
  }[];
  extraFieldsTitle?: string;
  visible: boolean;
  onClose: () => any;
  onSubmit: (form: {
    fields: T[];
    extraFields?: string[];
    assinaturas?: AssinaturasItem[];
    groupByFields?: T[];
  }) => any;
  isLoadingSave?: boolean;
  primaryButtonText?: string;
  title?: string;
  includeAssinaturas?: boolean;
  showFieldsTitle?: boolean;
}

function ModalSelectFieldsRelatorio<T>({
  fields,
  groupByFields,
  extraFields,
  extraFieldsTitle,
  visible,
  onClose,
  onSubmit,
  isLoadingSave,
  primaryButtonText = "Exportar",
  title = "Selecione as informações que aparecerão no relatório",
  includeAssinaturas = true,
  showFieldsTitle = true,
}: ModalSelectFieldsRelatorioProps<T>) {
  const { watch, setValue, control } = useForm<{
    assinaturas: AssinaturasItem[];
  }>();
  const [selectedFields, setSelectedFields] = useState(
    fields
      .filter((field) => field.defaultChecked !== false)
      .map((field) => field.value)
  );

  const [selectedExtraFields, setSelectedExtraFields] = useState(
    extraFields
      ?.filter((field) => field.defaultChecked !== false)
      .map((field) => field.value) || []
  );

  const [selectedGroupByFields, setSelectedGroupByFields] = useState<T[]>([]);

  useEffect(() => {
    setSelectedFields(
      fields
        .filter((field) => field.defaultChecked !== false)
        .map((field) => field.value)
    );
  }, [fields, visible]);

  useEffect(() => {
    setSelectedExtraFields(
      extraFields
        ?.filter((field) => field.defaultChecked !== false)
        .map((field) => field.value) || []
    );
  }, [extraFields, visible]);

  const onToggleField = (field: T) => {
    setSelectedFields((state) =>
      state.includes(field)
        ? state.filter((f) => f !== field)
        : state.concat([field])
    );
  };

  const onToggleExtraField = (field: string) => {
    setSelectedExtraFields((state) =>
      state.includes(field)
        ? state.filter((f) => f !== field)
        : state.concat([field])
    );
  };

  const onToggleGroupByFields = (field: T) => {
    setSelectedGroupByFields((state) =>
      state.includes(field)
        ? state.filter((f) => f !== field)
        : state.concat([field])
    );
  };

  const assinaturas = watch("assinaturas");

  return (
    <Modal
      destroyOnClose
      width="700px"
      title={title}
      footer={
        <FooterModal
          registerAnother={false}
          setRegisterAnother={() => null}
          onSubmit={() =>
            onSubmit({
              fields: selectedFields,
              extraFields: selectedExtraFields,
              assinaturas,
              groupByFields: selectedGroupByFields,
            })
          }
          onClose={onClose}
          isLoadingSave={isLoadingSave}
          showRegisterAnother={false}
          primaryButtonText={primaryButtonText}
          buttonCancelarText="Cancelar"
        />
      }
      centered
      bodyStyle={{
        height: "fit-content",
        overflow: "auto",
      }}
      visible={visible}
      onCancel={onClose}
    >
      <Flex flexDir="row">
        {fields.length ? (
          <Flex flexDir="column" gridGap={4} mt={-4}>
            {showFieldsTitle ? (
              <Text fontWeight="medium" mb={-2}>
                Colunas
              </Text>
            ) : null}
            {fields.map((field) => (
              <Checkbox
                key={`${field.value}`}
                mr="auto"
                isChecked={selectedFields.includes(field.value)}
                onChange={() => onToggleField(field.value)}
              >
                {field.label}
              </Checkbox>
            ))}
          </Flex>
        ) : null}

        {groupByFields?.length ? (
          <Flex flexDir="column" gridGap={4} mt={-4}>
            <Text fontWeight="medium" mb={-2}>
              Agrupar por
            </Text>
            {groupByFields.map((field) => (
              <Checkbox
                key={`${field.value}`}
                mr="auto"
                isChecked={selectedGroupByFields.includes(field.value)}
                onChange={() => onToggleGroupByFields(field.value)}
              >
                {field.label}
              </Checkbox>
            ))}
          </Flex>
        ) : null}

        {extraFields?.length ? (
          <Flex flexDir="column" gridGap={4} mt={-4} ml="100px">
            <Text fontWeight="medium" mb={-2}>
              {extraFieldsTitle}
            </Text>
            {extraFields.map((field) => (
              <Checkbox
                key={`${field.value}`}
                mr="auto"
                isChecked={selectedExtraFields.includes(field.value)}
                onChange={() => onToggleExtraField(field.value)}
              >
                {field.label}
              </Checkbox>
            ))}
          </Flex>
        ) : null}
      </Flex>

      {includeAssinaturas ? (
        <FormAssinaturas
          setValue={setValue}
          control={control}
          formAssinaturas={assinaturas}
        />
      ) : null}
    </Modal>
  );
}

export default ModalSelectFieldsRelatorio;
